import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useHistory } from 'react-router-dom';
import {
  Box,
  Grid,
  Stack,
  Button,
  TextField,
  MenuItem,
  Typography,
} from '@mui/material';
import CloudUploadOutlinedIcon from '@mui/icons-material/CloudUploadOutlined';
import {
  PageHeader,
  DetailCard,
  PurchaseInvoiceLineItemRow,
  LineItemsTable,
  LineItemSearchBar,
  BarcodeScannerModal,
  FileImportModal,
  MissingBarcodeDialog,
} from '../components';
import { getMenuPricesByInventoryUuids } from '../api/products';
import {
  describeLine,
  findLinesMissingBarcode,
  generateUniqueBarcodes,
} from '../utils/barcode';
import {
  findSkuConflicts,
  nextLineItemId,
  snapshotBeforeLink,
  unlinkPatch,
  withoutSearchBarcode,
} from '../utils/lineItems';
import { useCreatePurchaseInvoice } from '../hooks/usePurchaseInvoices';
import { useCostPreview } from '../hooks/useCostPreview';
import { useVendors } from '../hooks/useVendors';
import { useWarehouses } from '../hooks/useWarehouses';
import { useCategories } from '../hooks/useCategories';
import { useBusinessUuid } from '../useBusinessUuid';
import { colors, formatCurrency, SHOW_WAREHOUSE_FIELDS } from '../constants';

const emptyItem = () => ({
  // Stable identity, used as the React key and to address the line in state.
  id: nextLineItemId(),
  search: '',
  sku: '',
  name: '',
  barcodes: [],
  productType: 'product',
  orderedQuantity: '',
  unitCost: '',
  // MMS menu/selling price (optional). Distinct from unitCost (purchase cost).
  sellingPrice: '',
  unit: 'each',
  batchNumber: '',
  expiryDate: '',
  categoryId: null,
  categoryName: null,
  selectedItem: null,
  // Field values from before this line was linked to an existing product, put
  // back when the link is removed.
  beforeLink: null,
  // Existing product found to already use this line's SKU, offered as a link.
  skuConflict: null,
  // Cost-handling choice for existing products (ignored for new products).
  costMode: 'update',
  // Per-field validation messages, keyed by field. Kept on the item itself so
  // messages stay aligned with their line as items are added/removed.
  errors: {},
});

export default function PurchaseInvoiceCreate() {
  const history = useHistory();
  const businessUuid = useBusinessUuid();
  const createInvoice = useCreatePurchaseInvoice();
  const { data: vendors } = useVendors({ active: true });
  const { data: warehouses = [] } = useWarehouses();
  const { data: categories = [] } = useCategories(businessUuid);

  const [externalInvoiceNumber, setExternalInvoiceNumber] = useState('');
  const [vendorUuid, setVendorUuid] = useState('');
  const [warehouseUuid, setWarehouseUuid] = useState('');
  const [invoiceDate, setInvoiceDate] = useState(new Date().toISOString().split('T')[0]);
  const [taxAmount, setTaxAmount] = useState('');
  const [shippingCost, setShippingCost] = useState('');
  const [notes, setNotes] = useState('');
  const [items, setItems] = useState([]);
  const [scannerOpen, setScannerOpen] = useState(false);
  const [scanTargetId, setScanTargetId] = useState(null);
  const [importOpen, setImportOpen] = useState(false);
  const [error, setError] = useState(null);
  // Indexes of new lines without a barcode, pending the user's choice.
  const [missingBarcodeLines, setMissingBarcodeLines] = useState(null);

  const vendorOptions = useMemo(
    () => (vendors || []).map((v) => ({ value: v.uuid, label: v.name })),
    [vendors]
  );
  const warehouseOptions = useMemo(
    () => warehouses.filter((w) => w.isActive).map((w) => ({ value: w.uuid, label: w.name })),
    [warehouses]
  );
  // Resolving a line's category by scanning the list would cost one pass per
  // line on every render of the form.
  const categoryById = useMemo(
    () => new Map(categories.map((c) => [c.id, c])),
    [categories],
  );

  const subtotal = useMemo(
    () =>
      items.reduce((sum, it) => {
        const qty = parseFloat(it.orderedQuantity) || 0;
        const cost = parseFloat(it.unitCost) || 0;
        return sum + qty * cost;
      }, 0),
    [items]
  );
  const total = subtotal + (parseFloat(taxAmount) || 0) + (parseFloat(shippingCost) || 0);
  const totalQuantity = useMemo(
    () => items.reduce((sum, it) => sum + (parseFloat(it.orderedQuantity) || 0), 0),
    [items],
  );
  const costPreviews = useCostPreview(items, 'orderedQuantity');

  // Line inputs register themselves here by line id and field so a newly added
  // line can be focused without every row having to know its position in the
  // list.
  const lineInputsRef = useRef(new Map());
  const registerLineInput = useCallback((id, field, element) => {
    const key = `${id}:${field}`;
    if (element) lineInputsRef.current.set(key, element);
    else lineInputsRef.current.delete(key);
  }, []);
  const [pendingFocus, setPendingFocus] = useState(null);
  useEffect(() => {
    if (!pendingFocus) return;
    lineInputsRef.current.get(`${pendingFocus.id}:${pendingFocus.field}`)?.focus();
    setPendingFocus(null);
  }, [pendingFocus]);

  // `updates` may be an object or, when the patch depends on the line's current
  // values, a function of that line. The function form lets the rows keep stable
  // handlers, which is what keeps them from re-rendering on unrelated edits.
  const updateItem = useCallback(
    (id, updates) =>
      setItems((prev) =>
        prev.map((it) =>
          it.id === id
            ? { ...it, ...(typeof updates === 'function' ? updates(it) : updates) }
            : it,
        ),
      ),
    [],
  );
  // New lines go to the top of the table, under the "Add item" action;
  // focusing the new line's search box lets the user type/scan straight away.
  const addItem = useCallback(() => {
    const item = emptyItem();
    setItems((prev) => [item, ...prev]);
    setPendingFocus({ id: item.id, field: 'search' });
  }, []);
  // A code that matched no barcode starts a new product: the line gets the
  // code as its barcode, and focus goes to Name, the next field the user has
  // to fill in.
  const addNewItemWithBarcode = useCallback((code) => {
    const item = { ...emptyItem(), barcodes: [code] };
    setItems((prev) => [item, ...prev]);
    setPendingFocus({ id: item.id, field: 'name' });
  }, []);
  const removeItem = useCallback(
    (id) => setItems((prev) => prev.filter((it) => it.id !== id)),
    [],
  );

  // Prefill each matched line's Price with the product's CURRENT menu selling
  // price from MMS (fetched via api01). Best-effort: only fills lines that are
  // still blank, and never blocks the form if the lookup fails.
  const prefillSellingPrices = useCallback(async (uuids) => {
    const list = (uuids || []).filter(Boolean);
    if (!list.length) return;
    try {
      const prices = await getMenuPricesByInventoryUuids(list);
      setItems((prev) =>
        prev.map((it) => {
          const uuid = it.selectedItem?.uuid;
          if (
            uuid &&
            prices[uuid] != null &&
            (it.sellingPrice === '' || it.sellingPrice == null)
          ) {
            return { ...it, sellingPrice: String(prices[uuid]) };
          }
          return it;
        }),
      );
    } catch (e) {
      console.error('Failed to load current menu price(s)', e);
    }
  }, []);

  const handleItemSelected = useCallback(
    (id, item) => {
      const updates = {
        search: item.sku,
        sku: item.sku,
        name: item.name,
        unit: item.unit,
        barcodes: item.barcodes || [],
        // Existing product: only the category id comes from inventory; the
        // display name is resolved from the categories list. Read-only for
        // existing items.
        categoryId: item.categoryId ?? null,
        categoryName: null,
        selectedItem: item,
        productType: item.type,
        // Changing the selected product must reset the menu price: clear the
        // previous product's price so it is refilled below with THIS product's
        // current MMS price (or left blank if that product has no menu price).
        sellingPrice: '',
      };
      // Prefill the unit cost from the product's price when available.
      if (item.unitPrice != null && Number.isFinite(Number(item.unitPrice))) {
        updates.unitCost = String(item.unitPrice);
      }
      setItems((prev) =>
        prev.map((line) =>
          line.id === id
            ? {
                ...line,
                ...updates,
                // Keep the first snapshot: picking a different product
                // afterwards must still undo back to what the user had typed.
                beforeLink:
                  line.beforeLink ?? snapshotBeforeLink(withoutSearchBarcode(line)),
                skuConflict: null,
                errors: { ...(line.errors || {}), sku: undefined },
              }
            : line,
        ),
      );
      // Prefill the menu selling price from MMS for this existing product.
      if (item.uuid) prefillSellingPrices([item.uuid]);
    },
    [prefillSellingPrices],
  );

  const handleUnlink = useCallback(
    (id) => updateItem(id, (line) => unlinkPatch(line, emptyItem())),
    [updateItem],
  );

  // Adds an existing product as a new line, linked exactly as if it had been
  // picked in that line's own search box.
  const prependLinkedItem = useCallback(
    (item) => {
      const line = emptyItem();
      setItems((prev) => [line, ...prev]);
      handleItemSelected(line.id, item);
    },
    [handleItemSelected],
  );

  const handleBarcodeScanRequest = useCallback((id) => {
    setScanTargetId(id);
    setScannerOpen(true);
  }, []);

  // The search bar's scan button targets no line, so a found item is added as a new line.
  const handleSearchBarScan = useCallback(() => {
    setScanTargetId(null);
    setScannerOpen(true);
  }, []);

  const handleBarcodeFound = (item) => {
    if (scanTargetId && items.some((it) => it.id === scanTargetId)) {
      handleItemSelected(scanTargetId, item);
    } else {
      prependLinkedItem(item);
    }
  };

  const handleFileImport = (rows) => {
    setItems((prev) => [
      ...prev,
      ...rows.map((row) => ({
        ...emptyItem(),
        search: row.sku || '',
        sku: row.sku,
        name: row.name,
        barcodes: row.barcode ? [row.barcode] : [],
        productType: row.productType,
        orderedQuantity: row.quantity,
        unitCost: row.unitCost,
        sellingPrice: row.sellingPrice != null ? String(row.sellingPrice) : '',
        unit: row.unit || '',
        expiryDate: row.expiryDate || '',
        selectedItem: null,
      })),
    ]);
  };

  const handleSubmit = async () => {
    setError(null);
    if (items.length === 0) return setError('Add at least one line item.');

    // Validate every line and attach a message to each individual field that is
    // missing/invalid so it renders directly under that field (instead of one
    // generic message that stops at the first bad line).
    let hasFieldErrors = false;
    const validated = items.map((it) => {
      const errors = {};
      if (!it.sku.trim() && !it.name.trim()) {
        errors.identity = 'Name or SKU is required.';
      }
      const qty = parseFloat(it.orderedQuantity);
      if (!Number.isFinite(qty) || qty <= 0) {
        errors.orderedQuantity = 'Quantity must be greater than zero.';
      }
      const cost = parseFloat(it.unitCost);
      if (!Number.isFinite(cost) || cost <= 0) {
        errors.unitCost = 'Unit cost must be greater than zero.';
      }
      if (it.sellingPrice === '' || it.sellingPrice == null) {
        errors.sellingPrice = 'Price is required.';
      } else {
        const selling = parseFloat(it.sellingPrice);
        if (!Number.isFinite(selling) || selling < 0) {
          errors.sellingPrice = 'Price must be zero or greater.';
        }
      }
      const productType = it.selectedItem?.type || it.productType;
      if (!it.selectedItem && productType === 'product' && !it.categoryId) {
        errors.category = 'Menu category is required for new products.';
      }
      if (Object.keys(errors).length) hasFieldErrors = true;
      return { ...it, errors };
    });

    if (hasFieldErrors) {
      setItems(validated);
      return undefined;
    }

    // A line with nothing linked creates a new product, and a SKU is unique per
    // business, so a SKU that is already taken is caught here: the user links
    // the line to that product or gives it a different SKU, instead of the
    // whole invoice being rejected.
    let skuConflicts;
    try {
      skuConflicts = await findSkuConflicts(validated, businessUuid);
    } catch (e) {
      setError(
        e?.response?.data?.message ||
          'Could not check the SKUs against existing products. Nothing was saved.',
      );
      return undefined;
    }
    if (skuConflicts.size > 0) {
      setItems(
        validated.map((it, idx) => {
          const match = skuConflicts.get(idx);
          if (!match) return it;
          return {
            ...it,
            skuConflict: match,
            errors: {
              ...it.errors,
              sku: `SKU "${it.sku}" already belongs to "${match.name}".`,
            },
          };
        }),
      );
      return undefined;
    }
    // Nothing left to flag: drop the messages from an earlier attempt.
    setItems(validated);

    // New items with no barcode cannot be scanned later, so ask what to do
    // before creating them.
    const missing = findLinesMissingBarcode(validated);
    if (missing.length > 0) {
      setMissingBarcodeLines(missing);
      return undefined;
    }

    return submitInvoice(validated);
  };

  const submitInvoice = async (lineItems) => {
    try {
      const result = await createInvoice.mutateAsync({
        businessUuid,
        externalInvoiceNumber: externalInvoiceNumber.trim() || undefined,
        vendorUuid: vendorUuid || undefined,
        warehouseUuid: warehouseUuid || undefined,
        invoiceDate,
        taxAmount: parseFloat(taxAmount) || 0,
        shippingCost: parseFloat(shippingCost) || 0,
        notes: notes || undefined,
        items: lineItems.map((it) => ({
          // Sent only for a line linked to an existing item. Without it the
          // backend creates a new item instead of matching one.
          productUuid: it.selectedItem?.uuid || undefined,
          sku: it.sku,
          name: it.name,
          barcodes: it.barcodes?.length ? it.barcodes : undefined,
          productType: it.selectedItem?.type || it.productType,
          orderedQuantity: parseFloat(it.orderedQuantity) || 0,
          unitCost: parseFloat(it.unitCost) || 0,
          // Optional MMS selling price; omitted when left blank so the menu
          // price is left untouched.
          sellingPrice:
            it.sellingPrice !== '' &&
            it.sellingPrice != null &&
            Number.isFinite(parseFloat(it.sellingPrice))
              ? parseFloat(it.sellingPrice)
              : undefined,
          unit: it.unit || undefined,
          batchNumber: it.batchNumber || undefined,
          expiryDate: it.expiryDate || undefined,
          categoryId: it.selectedItem ? undefined : it.categoryId || undefined,
          categoryName: it.selectedItem
            ? undefined
            : it.categoryName || undefined,
          // Cost handling only applies to existing (linked) products.
          costMode: it.selectedItem ? it.costMode || 'update' : undefined,
        })),
      });
      history.push(`/inventory-management/purchase-invoices/${result.uuid}`);
    } catch (e) {
      setError(e?.response?.data?.message || e.message || 'Something went wrong');
    }
    return undefined;
  };

  const handleGenerateMissingBarcodes = async () => {
    const indexes = missingBarcodeLines || [];
    try {
      const codes = await generateUniqueBarcodes(indexes.length, businessUuid);
      const next = items.map((it, idx) => {
        const position = indexes.indexOf(idx);
        return position === -1 ? it : { ...it, barcodes: [codes[position]] };
      });
      setItems(next);
      setMissingBarcodeLines(null);
      await submitInvoice(next);
    } catch (e) {
      setMissingBarcodeLines(null);
      setError(
        e?.response?.data?.message || 'Failed to generate barcodes. Nothing was created.',
      );
    }
  };

  return (
    <>
      <PageHeader title="New Invoice" backTo="/inventory-management/purchase-invoices" />

      {error && (
        <Typography variant="body2" sx={{ color: colors.error, mb: 2 }}>
          {error}
        </Typography>
      )}

      <Stack spacing={2.5}>
        <DetailCard title="Invoice Details">
          <Typography variant="caption" sx={{ color: colors.gray500, display: 'block', mb: 1.5 }}>
            Invoice number will be generated automatically.
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6} md={3}>
              <TextField select label="Vendor" value={vendorUuid} onChange={(e) => setVendorUuid(e.target.value)} fullWidth size="small">
                <MenuItem value="">Select vendor</MenuItem>
                {vendorOptions.map((v) => (
                  <MenuItem key={v.value} value={v.value}>{v.label}</MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <TextField label="Vendor Invoice" value={externalInvoiceNumber} onChange={(e) => setExternalInvoiceNumber(e.target.value)} fullWidth size="small" placeholder="Vendor invoice #" />
            </Grid>
            {SHOW_WAREHOUSE_FIELDS && (
              <Grid item xs={12} sm={6} md={3}>
                <TextField select label="Warehouse (optional)" value={warehouseUuid} onChange={(e) => setWarehouseUuid(e.target.value)} fullWidth size="small">
                  <MenuItem value="">Select warehouse</MenuItem>
                  {warehouseOptions.map((w) => (
                    <MenuItem key={w.value} value={w.value}>{w.label}</MenuItem>
                  ))}
                </TextField>
              </Grid>
            )}
            <Grid item xs={12} sm={6} md={3}>
              <TextField label="Invoice Date" type="date" InputLabelProps={{ shrink: true }} value={invoiceDate} onChange={(e) => setInvoiceDate(e.target.value)} fullWidth size="small" />
            </Grid>
          </Grid>
        </DetailCard>

        <DetailCard
          title={`Line Items ${items.length ? `(${items.length})` : ''}`}
          action={
            <Button size="small" startIcon={<CloudUploadOutlinedIcon />} onClick={() => setImportOpen(true)} sx={{ textTransform: 'none' }}>
              Import CSV
            </Button>
          }
        >
          <LineItemSearchBar
            onItemSelected={prependLinkedItem}
            onCreateNew={addNewItemWithBarcode}
            onBarcodeScan={handleSearchBarScan}
          />
          <Box sx={{ mt: 1.5 }}>
            <LineItemsTable
              itemCount={items.length}
              totalQuantity={totalQuantity}
              totalCost={subtotal}
              onAddItem={addItem}
              emptyMessage={'No items added yet. Use "Add item" or "Import CSV".'}
            >
              {items.map((item) => {
                const preview = costPreviews.get(item.id);
                return (
                  <PurchaseInvoiceLineItemRow
                    key={item.id}
                    item={item}
                    categories={categories}
                    categoryById={categoryById}
                    onUpdate={updateItem}
                    onRemove={removeItem}
                    onItemSelected={handleItemSelected}
                    onUnlink={handleUnlink}
                    onBarcodeScan={handleBarcodeScanRequest}
                    registerLineInput={registerLineInput}
                    previewStatus={preview?.status}
                    previewNewCost={preview?.newCost}
                    previewError={preview?.error}
                  />
                );
              })}
            </LineItemsTable>
          </Box>
        </DetailCard>

        <DetailCard>
          <Grid container spacing={2.5} alignItems="flex-end">
            <Grid item xs={12} md={7}>
              <Stack spacing={2}>
                <TextField label="Notes" value={notes} onChange={(e) => setNotes(e.target.value)} fullWidth multiline minRows={2} size="small" />
                <Stack direction="row" spacing={2}>
                  <TextField label="Tax Amount" type="number" value={taxAmount} onChange={(e) => setTaxAmount(e.target.value)} fullWidth size="small" />
                  <TextField label="Shipping Cost" type="number" value={shippingCost} onChange={(e) => setShippingCost(e.target.value)} fullWidth size="small" />
                </Stack>
              </Stack>
            </Grid>
            <Grid item xs={12} md={5}>
              <Stack spacing={0.5}>
                <Stack direction="row" justifyContent="space-between">
                  <Typography variant="body2" sx={{ color: colors.gray600 }}>Subtotal</Typography>
                  <Typography variant="body2">{formatCurrency(subtotal)}</Typography>
                </Stack>
                {(parseFloat(taxAmount) || 0) > 0 && (
                  <Stack direction="row" justifyContent="space-between">
                    <Typography variant="body2" sx={{ color: colors.gray600 }}>Tax</Typography>
                    <Typography variant="body2">{formatCurrency(parseFloat(taxAmount))}</Typography>
                  </Stack>
                )}
                {(parseFloat(shippingCost) || 0) > 0 && (
                  <Stack direction="row" justifyContent="space-between">
                    <Typography variant="body2" sx={{ color: colors.gray600 }}>Shipping</Typography>
                    <Typography variant="body2">{formatCurrency(parseFloat(shippingCost))}</Typography>
                  </Stack>
                )}
                <Stack direction="row" justifyContent="space-between" sx={{ pt: 1, borderTop: `1px solid ${colors.border}` }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>Total</Typography>
                  <Typography variant="subtitle2" sx={{ fontWeight: 700, color: colors.primary }}>
                    {formatCurrency(total)}
                  </Typography>
                </Stack>
                <Box sx={{ pt: 1.5 }}>
                  <Button
                    fullWidth
                    variant="contained"
                    onClick={handleSubmit}
                    disabled={createInvoice.isPending || items.length === 0}
                    sx={{ backgroundColor: colors.primary, textTransform: 'none', fontWeight: 600, py: 1.25 }}
                  >
                    Create Invoice
                  </Button>
                </Box>
              </Stack>
            </Grid>
          </Grid>
        </DetailCard>
      </Stack>

      <BarcodeScannerModal
        open={scannerOpen}
        onClose={() => setScannerOpen(false)}
        onItemFound={handleBarcodeFound}
        onBarcodeNotFound={(code) => {
          if (scanTargetId && items.some((it) => it.id === scanTargetId)) {
            setItems((prev) =>
              prev.map((it) =>
                it.id === scanTargetId
                  ? {
                      ...it,
                      search: code,
                      selectedItem: null,
                      barcodes: it.barcodes.includes(code)
                        ? it.barcodes
                        : [...it.barcodes, code],
                    }
                  : it
              )
            );
          } else {
            setItems((prev) => [
              ...prev,
              { ...emptyItem(), search: code, barcodes: [code] },
            ]);
          }
          setError(`No product found for barcode "${code}". Added as a new barcode.`);
        }}
      />

      <FileImportModal
        open={importOpen}
        onClose={() => setImportOpen(false)}
        onImport={handleFileImport}
      />

      <MissingBarcodeDialog
        open={Boolean(missingBarcodeLines?.length)}
        lines={(missingBarcodeLines || []).map((idx) =>
          describeLine(items[idx] || {}, idx),
        )}
        loading={createInvoice.isPending}
        onCancel={() => setMissingBarcodeLines(null)}
        onAutogenerate={handleGenerateMissingBarcodes}
        onContinue={() => {
          setMissingBarcodeLines(null);
          submitInvoice(items);
        }}
      />
    </>
  );
}
